import { use, useCallback, useEffect, useRef, useState } from 'react'
import { ChartCursorProviderContext } from '@/components/shared/analytics/chart-cursor/context/context'
import { ChartTimeframeProviderContext } from '@/components/shared/analytics/chart-timeframe/context/context'
import { getIntervalInSeconds } from '@/config/charts'

const MIN_ZOOM_SECONDS = 60

type DragState = {
  refAreaLeft: number | null
  refAreaRight: number | null
}

type UseChartZoomOptions = {
  intervalMs?: number
}

export const useChartZoom = (options: UseChartZoomOptions = {}) => {
  const { intervalMs } = options
  const { setTimeframe } = use(ChartTimeframeProviderContext)
  const { cursorTimestamp, setCursorTimestamp } = use(
    ChartCursorProviderContext
  )
  const [drag, setDrag] = useState<DragState>({
    refAreaLeft: null,
    refAreaRight: null,
  })
  const isDragging = useRef(false)
  const hasSkippedFirstMove = useRef(false)
  const selectionStartLabel = useRef<number | null>(null)

  const isSelecting = drag.refAreaLeft !== null && drag.refAreaRight !== null

  const onMouseDown = useCallback(
    (e: { activeLabel?: string | number } | null | undefined) => {
      if (!e?.activeLabel) {
        return
      }
      isDragging.current = true
      hasSkippedFirstMove.current = false
      selectionStartLabel.current = Number(e.activeLabel)
    },
    []
  )

  const onMouseMove = useCallback(
    (e: { activeLabel?: string | number } | null | undefined) => {
      if (!e?.activeLabel) {
        return
      }

      const label = Number(e.activeLabel)
      setCursorTimestamp(label)

      if (!isDragging.current) {
        return
      }
      if (!hasSkippedFirstMove.current) {
        hasSkippedFirstMove.current = true
        return
      }

      const startLabel = selectionStartLabel.current
      if (startLabel === null) {
        return
      }

      const halfInterval = (intervalMs ?? 0) / 2
      const isForward = label >= startLabel
      const left = isForward ? startLabel - halfInterval : label - halfInterval
      const right = isForward ? label + halfInterval : startLabel + halfInterval

      setDrag({ refAreaLeft: left, refAreaRight: right })
    },
    [setCursorTimestamp, intervalMs]
  )

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) {
      return
    }
    isDragging.current = false
    selectionStartLabel.current = null
    setCursorTimestamp(null)

    setDrag((prev) => {
      const { refAreaLeft, refAreaRight } = prev
      if (refAreaLeft === null || refAreaRight === null) {
        return { refAreaLeft: null, refAreaRight: null }
      }

      const left = Math.min(refAreaLeft, refAreaRight)
      const right = Math.max(refAreaLeft, refAreaRight)
      const diffInSeconds = (right - left) / 1000

      if (diffInSeconds < MIN_ZOOM_SECONDS) {
        return { refAreaLeft: null, refAreaRight: null }
      }

      const startDate = new Date(left)
      const endDate = new Date(right)

      setTimeframe({
        startDate,
        endDate,
        intervalInSeconds: getIntervalInSeconds(startDate, endDate),
      })

      return { refAreaLeft: null, refAreaRight: null }
    })
  }, [setTimeframe, setCursorTimestamp])

  const onMouseLeave = useCallback(() => {
    if (!isDragging.current) {
      setCursorTimestamp(null)
    }
  }, [setCursorTimestamp])

  useEffect(() => {
    if (!isSelecting) {
      return
    }
    const handleDocumentMouseUp = () => onMouseUp()
    document.addEventListener('mouseup', handleDocumentMouseUp)
    return () => document.removeEventListener('mouseup', handleDocumentMouseUp)
  }, [isSelecting, onMouseUp])

  const chartProps = {
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
  }

  const referenceAreaProps = isSelecting
    ? {
        x1: drag.refAreaLeft!,
        x2: drag.refAreaRight!,
        fill: 'hsl(var(--chart-1))',
        fillOpacity: 0.15,
        strokeOpacity: 0,
      }
    : null

  return { chartProps, referenceAreaProps, isSelecting, cursorTimestamp }
}
